// =====================================================================
// QuickPaymentDialog (S91) — "Registrar recebimento" de balcão: UMA tela, um
// clique. Recebimento avulso (sem orçamento/aprovação/parcela) + comprovante
// da maquininha no mesmo ato. Cai automático no caixa da unidade (S91) e conta
// nas metas/pagamentos/extrato. O fluxo completo (orçamento→conta a receber→
// baixa) continua existindo pra parcelamento/convênio. Padrão MUI v4.
//
// [S103] MULTI-PACIENTE (casal/família): o par Paciente+Valor virou REPETÍVEL.
// 1 paciente = igual a sempre (createPayment). Vários = recebimento DIVIDIDO
// (createPaymentBatch → 1 pagamento por paciente, cada com seu valor), o mesmo
// pagador/forma/unidade/descrição em todos, e o comprovante anexado em cada.
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  CircularProgress
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  createPayment,
  createPaymentBatch,
  uploadPaymentReceipt,
  listSellers,
  fmtMoney,
  PAYMENT_METHODS,
  CARD_BRANDS,
  financeErrorMessage
} from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";
import PatientAmountRow from "./PatientAmountRow";

const emptyRow = () => ({ patient: null, amount: "" });
const parseAmt = (a) => Number(String(a).replace(",", ".")) || 0;

const QuickPaymentDialog = ({
  open,
  onClose,
  onSaved,
  units = [],
  defaultUnitId, // number | undefined (unidade ativa)
  hasUnits = false
}) => {
  const isMobile = useIsMobile();
  // [S103] linhas paciente+valor (1 = comum; várias = casal/família dividido)
  const [rows, setRows] = useState([emptyRow()]);

  const [unitId, setUnitId] = useState("");
  const [method, setMethod] = useState("dinheiro");
  const [cardBrand, setCardBrand] = useState("visa");
  const [cardInstallments, setCardInstallments] = useState("1");
  const [description, setDescription] = useState("");
  const [sellerName, setSellerName] = useState(""); // [S92] quem fez a venda
  const [sellers, setSellers] = useState([]);        // [S92] lista (por empresa)
  const [payerName, setPayerName] = useState("");    // [S96] quem pagou (!= paciente)
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows([emptyRow()]);
    setUnitId(defaultUnitId != null ? String(defaultUnitId) : "");
    setMethod("dinheiro");
    setCardBrand("visa");
    setCardInstallments("1");
    setDescription("");
    setSellerName("");
    setPayerName("");
    setReceiptFile(null);
    setSaving(false);
  }, [open, defaultUnitId]);

  // [S92] Carrega os vendedores da empresa ao abrir. Lista vazia => o campo
  // "Quem fez a venda?" nem aparece (CLEE/Rostand ficam sem dropdown).
  useEffect(() => {
    if (!open) return;
    let alive = true;
    listSellers()
      .then((d) => { if (alive) setSellers(Array.isArray(d && d.sellers) ? d.sellers : []); })
      .catch(() => { if (alive) setSellers([]); });
    return () => { alive = false; };
  }, [open]);

  const isCard = method === "debito" || method === "credito";
  const isSplit = rows.length > 1;
  const total = rows.reduce((s, r) => s + parseAmt(r.amount), 0);

  const updateRow = (i, next) => setRows((rs) => rs.map((r, idx) => (idx === i ? next : r)));
  const addRow = () => setRows((rs) => (rs.length >= 12 ? rs : [...rs, emptyRow()]));
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    // valida cada linha (paciente + valor > 0)
    for (let i = 0; i < rows.length; i++) {
      if (!rows[i].patient) {
        toast.error(`Selecione o paciente${isSplit ? ` ${i + 1}` : ""}.`);
        return;
      }
      if (parseAmt(rows[i].amount) <= 0) {
        toast.error(`Informe um valor válido${isSplit ? ` pro paciente ${i + 1}` : ""}.`);
        return;
      }
    }
    if (hasUnits && !unitId) {
      toast.error("Selecione a unidade.");
      return;
    }
    setSaving(true);
    try {
      // campos COMPARTILHADOS por todos os pacientes do recebimento
      const shared = {
        method,
        notes: description || undefined,
        sellerName: sellerName || undefined, // [S92] quem fez a venda (opcional)
        payerName: payerName || undefined    // [S96] quem pagou (!= paciente)
      };
      if (hasUnits && unitId) shared.unitId = Number(unitId);
      if (isCard) {
        shared.cardBrand = cardBrand;
        if (method === "credito") shared.cardInstallments = Number(cardInstallments) || 1;
      }

      let createdPayments = [];
      if (isSplit) {
        // [S103] recebimento dividido → 1 pagamento por paciente (mesmo pagador/forma/unidade)
        const res = await createPaymentBatch({
          ...shared,
          items: rows.map((r) => ({ patientId: r.patient.id, amount: parseAmt(r.amount) }))
        });
        createdPayments = Array.isArray(res && res.payments) ? res.payments : [];
      } else {
        const payment = await createPayment({
          ...shared,
          patientId: rows[0].patient.id,
          amount: parseAmt(rows[0].amount)
        });
        if (payment && payment.id) createdPayments = [payment];
      }

      // Comprovante (opcional, 1 arquivo) — anexa em CADA pagamento criado. Se falhar,
      // o recebimento JÁ está salvo (só avisa), não perde nada.
      if (receiptFile && createdPayments.length) {
        let failed = 0;
        for (const p of createdPayments) {
          if (p && p.id) {
            try { await uploadPaymentReceipt(p.id, receiptFile); }
            catch (e) { failed += 1; }
          }
        }
        if (failed > 0) {
          toast.warn("Recebimento salvo, mas o comprovante não subiu em tudo. Anexe depois em Pagamentos.");
        }
      }

      toast.success(
        isSplit
          ? `${createdPayments.length} recebimentos registrados (total ${fmtMoney(total)}). ✅`
          : `Recebimento de ${fmtMoney(total)} registrado. ✅`
      );
      onSaved && onSaved();
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Registrar recebimento</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Recebimento de balcão — o cliente pagou agora. Cai direto no caixa{hasUnits ? " da unidade" : ""}.
        </Typography>

        {/* [S103] Paciente + Valor (repetível: casal/família = recebimento dividido) */}
        {rows.map((r, i) => (
          <PatientAmountRow
            key={i}
            index={i}
            value={r}
            showRemove={rows.length > 1}
            onChange={(next) => updateRow(i, next)}
            onRemove={() => removeRow(i)}
          />
        ))}

        <Box display="flex" alignItems="center" style={{ gap: 12, marginTop: 2, flexWrap: "wrap" }}>
          <Button size="small" onClick={addRow} disabled={rows.length >= 12} style={{ textTransform: "none" }}>
            + Adicionar paciente (casal/família)
          </Button>
          {isSplit && (
            <Typography variant="body2" color="textSecondary">
              Total: <strong>{fmtMoney(total)}</strong> — vira {rows.length} recebimentos (1 por paciente).
            </Typography>
          )}
        </Box>

        <Box display="flex" style={{ gap: 12, flexWrap: "wrap", marginTop: 4 }}>
          {hasUnits && (
            <TextField
              select
              label="Unidade"
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              margin="dense"
              style={{ flexGrow: 1, minWidth: 130 }}
            >
              {units.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            label="Forma de pagamento"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            margin="dense"
            style={{ flexGrow: 1, minWidth: 180 }}
          >
            {PAYMENT_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
        </Box>

        {isCard && (
          <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
            <TextField select label="Bandeira" value={cardBrand} onChange={(e) => setCardBrand(e.target.value)}
              margin="dense" style={{ flexGrow: 1, minWidth: 130 }}>
              {CARD_BRANDS.filter((b) => b.value !== "todas").map((b) => (
                <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
              ))}
            </TextField>
            {method === "credito" && (
              <TextField label="Parcelas" type="number" value={cardInstallments}
                onChange={(e) => setCardInstallments(e.target.value)} margin="dense"
                inputProps={{ min: 1, max: 24 }} style={{ flexGrow: 1, minWidth: 130 }} />
            )}
          </Box>
        )}

        <TextField
          label="O que foi pago? (opcional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          margin="dense"
          placeholder="Ex.: Consulta, aplicação, procedimento…"
        />

        <TextField
          label="Quem pagou? (opcional, se diferente do paciente)"
          value={payerName}
          onChange={(e) => setPayerName(e.target.value)}
          fullWidth
          margin="dense"
          placeholder="Ex.: cônjuge/familiar que passou o cartão"
        />

        {sellers.length > 0 && (
          <TextField
            select
            label="Quem fez a venda? (opcional)"
            value={sellerName}
            onChange={(e) => setSellerName(e.target.value)}
            fullWidth
            margin="dense"
          >
            <MenuItem value=""><em>— não informar —</em></MenuItem>
            {sellers.map((s) => (
              <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
            ))}
          </TextField>
        )}

        <Box mt={2}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Comprovante da maquininha (opcional){isSplit ? " — anexado em cada paciente" : ""}
          </Typography>
          <Button component="label" variant="outlined" size="small">
            {receiptFile ? "Trocar arquivo" : "📎 Anexar comprovante"}
            <input
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                e.target.value = "";
                setReceiptFile(f || null);
              }}
            />
          </Button>
          {receiptFile && (
            <Typography variant="caption" style={{ marginLeft: 8 }}>
              {receiptFile.name} — <span
                style={{ color: "#c62828", cursor: "pointer" }}
                onClick={() => setReceiptFile(null)}
              >remover</span>
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {isSplit ? `Registrar ${rows.length} recebimentos` : "Registrar recebimento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickPaymentDialog;
